import { HsaConditionStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { HSA_MAX_LIST_ROWS } from "@redotech/redo-web-automations/automations/utils/layout-elements";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import { CollapseToggle, CollapsibleNodeData } from "./collapse-toggle";
import {
  flattenHsaConditionLeaves,
  getHsaConditionLeafAnswerText,
  getHsaConditionLeafQuestionLabel,
  HsaQuestionLookup,
} from "./hsa-condition-summary";
import * as NodeCSS from "./node.module.css";

export interface HsaConditionNodeData extends CollapsibleNodeData {
  step: PartialWithRequiredType<HsaConditionStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
  questionLookup: HsaQuestionLookup;
}

export const HsaConditionNode = memo(function HsaConditionNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: HsaConditionNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const entries = useMemo(
    () => flattenHsaConditionLeaves(data.step.condition),
    [data.step.condition],
  );
  const shown = entries.slice(0, HSA_MAX_LIST_ROWS);
  const overflow = entries.length - shown.length;

  return (
    <BaseNode
      containerClassName={NodeCSS.checkoutNode}
      icon={<RedoFeaturedIcon color="warning" Icon={GitBranchIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title={t("Condition")}
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Flex className={NodeCSS.fullWidth} dir="column" gap="sm">
        {shown.length ? (
          shown.map(({ leaf, connector }, index) => (
            <Flex dir="column" gap="xxs" key={index}>
              {connector && (
                <Text fontSize="xxs" textColor="secondary">
                  {connector}
                </Text>
              )}
              <Text className={NodeCSS.clampedValueText}>
                {getHsaConditionLeafQuestionLabel(leaf, data.questionLookup)}
              </Text>
              <Text fontSize="xxs" textColor="secondary">
                {t("Matching answer: {{answer}}", {
                  answer:
                    getHsaConditionLeafAnswerText(leaf, data.questionLookup) ??
                    "—",
                })}
              </Text>
            </Flex>
          ))
        ) : (
          <Text className={NodeCSS.placeholderText}>{t("No condition")}</Text>
        )}
        {overflow > 0 && (
          <Flex w="full">
            <RedoBadge
              color="gray"
              size="xs"
              text={t("+{{count}} more conditions", { count: overflow })}
            />
          </Flex>
        )}
      </Flex>
      <CollapseToggle
        descendantCount={data.descendantCount}
        isCollapsed={data.isCollapsed}
        toggleCollapse={data.toggleCollapse}
      />
      {!data.isCollapsed && (
        <Handle id="bottom" position={Position.Bottom} type="source" />
      )}
    </BaseNode>
  );
});
